import type { CharacterInfo, ModelInfo } from '@gofranz/gpt-muse-common';
import { ModelCapability } from '@gofranz/llm-core-common';
import {
  ActionIcon,
  Badge,
  Box,
  Center,
  CloseButton,
  Group,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  IconDownload,
  IconMicrophone,
  IconPhoto,
  IconPlayerStop,
  IconSend,
  IconSettings,
} from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useCooldown } from '../../hooks/useCooldown';
import type { ChatMessage, ChatMessageAttachment } from '../../lib/chat-db';
import { type ChatInputStatus, isInputDisabled } from '../../lib/chat-input-status';
import { CharacterSelector } from './CharacterSelector';
import { ModelSelector } from './ModelSelector';
import { SaveChatModal } from './SaveChatModal';
import {
  type ChatSettings,
  type ReasoningSettings,
  SettingsModal,
  type WebSearchSettings,
} from './SettingsModal';

interface ChatInputBarProps {
  selectedModel: string;
  isAuthenticated: boolean;
  /** Single source of truth for the input's interactive state. */
  status: ChatInputStatus;
  selectedCharacter?: string | null;
  models?: ModelInfo[];
  isLoadingModels?: boolean;
  messages: ChatMessage[];
  currentReasoningSettings?: ReasoningSettings;
  currentChatSettings?: ChatSettings;
  currentWebSearchSettings?: WebSearchSettings;
  inputPlaceholder?: string;
  characters?: CharacterInfo[];
  onSubmit: (message: string, attachment?: ChatMessageAttachment) => void;
  onStopStreaming?: () => void;
  onModelSelect?: (modelId: string) => void;
  onCharacterSelect?: (characterId: string | null) => void;
  onReasoningSettingsChange?: (modelId: string, settings: ReasoningSettings) => void;
  onChatSettingsChange?: (settings: ChatSettings) => void;
  onWebSearchSettingsChange?: (modelId: string, settings: WebSearchSettings) => void;
}

export function ChatInputBar({
  selectedModel,
  isAuthenticated,
  status,
  selectedCharacter,
  models = [],
  isLoadingModels = false,
  messages,
  currentReasoningSettings,
  currentChatSettings,
  currentWebSearchSettings,
  inputPlaceholder,
  characters = [],
  onSubmit,
  onStopStreaming,
  onModelSelect,
  onCharacterSelect,
  onReasoningSettingsChange,
  onChatSettingsChange,
  onWebSearchSettingsChange,
}: ChatInputBarProps) {
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [saveChatModalOpen, setSaveChatModalOpen] = useState(false);
  const [attachment, setAttachment] = useState<ChatMessageAttachment | null>(null);

  const imageInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);

  // Live countdown — only ticks when status carries an `until` date.
  const cooldownUntil =
    status.kind === 'cooldown' || status.kind === 'rate_limited' ? status.until : null;
  const { secondsLeft } = useCooldown(cooldownUntil);

  const isStreaming = status.kind === 'streaming';
  const isCooldown = status.kind === 'cooldown';
  const disabled = isInputDisabled(status);

  // Check if selected model supports vision (for image/audio input)
  const selectedModelInfo = models.find((m) => m.id === selectedModel);
  const supportsVision = selectedModelInfo?.capabilities?.includes(ModelCapability.Vision) ?? false;

  // Resolve placeholder + tooltip text from the current status.
  const placeholder = (() => {
    switch (status.kind) {
      case 'rate_limited':
        return status.reason;
      case 'cooldown':
        return secondsLeft > 0 ? `${status.reason} (${secondsLeft}s)` : status.reason;
      default:
        return inputPlaceholder || 'Type your message here...';
    }
  })();

  const submitTooltip = (() => {
    switch (status.kind) {
      case 'streaming':
        return 'Stop generation';
      case 'rate_limited':
        return status.reason;
      case 'cooldown':
        return secondsLeft > 0 ? `${status.reason} (retry in ${secondsLeft}s)` : status.reason;
      default:
        return 'Send message (Enter)';
    }
  })();

  const form = useForm({
    initialValues: {
      message: '',
    },
    validate: {
      message: (value) => (!value.trim() ? 'Message cannot be empty' : null),
    },
  });

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
    type: 'image' | 'audio'
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (5MB for images, 10MB for audio)
    const maxSize = type === 'image' ? 5 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`File too large. Maximum size: ${type === 'image' ? '5MB' : '10MB'}`);
      return;
    }

    // Read file as base64
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Extract base64 data (remove data URL prefix)
      const base64Data = result.split(',')[1];

      setAttachment({
        data: base64Data,
        mimeType: file.type,
        type,
      });
    };
    reader.onerror = () => {
      alert('Failed to read file');
    };
    reader.readAsDataURL(file);

    // Reset the input so the same file can be selected again
    event.target.value = '';
  };

  const handleRemoveAttachment = () => {
    setAttachment(null);
  };

  const handleSubmit = (values: { message: string }) => {
    if (!selectedModel || !values.message.trim() || disabled) return;

    onSubmit(values.message.trim(), attachment || undefined);
    form.reset();
    setAttachment(null); // Clear attachment after sending
  };

  // Attachment buttons disable for any non-ready status (same as before, just
  // collapsed: previously isStreaming || isLoading || isRateLimited || isOnConcurrencyCooldown).
  const attachmentDisabled = disabled || !!attachment || !supportsVision;

  // Send button disables for everything except `streaming` (which becomes Stop)
  // and `ready`. Previously: isLoading || isRateLimited || isOnConcurrencyCooldown.
  const sendDisabled =
    status.kind === 'loading' ||
    status.kind === 'rate_limited' ||
    status.kind === 'cooldown' ||
    !form.values.message.trim();

  return (
    <>
      <Center>
        <Box
          w="100%"
          style={{
            maxWidth: '900px',
            minWidth: 0,
            width: '100%',
          }}
        >
          <form onSubmit={form.onSubmit(handleSubmit)}>
            {/* Hidden file inputs */}
            <input
              ref={imageInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              style={{ display: 'none' }}
              onChange={(e) => handleFileSelect(e, 'image')}
            />
            <input
              ref={audioInputRef}
              type="file"
              accept="audio/wav,audio/mp3,audio/mpeg"
              style={{ display: 'none' }}
              onChange={(e) => handleFileSelect(e, 'audio')}
            />

            {/* Attachment preview */}
            {attachment && (
              <Group gap="xs" mb="xs">
                <Badge
                  size="lg"
                  variant="light"
                  color={attachment.type === 'image' ? 'blue' : 'grape'}
                  rightSection={
                    <CloseButton
                      size="xs"
                      onClick={handleRemoveAttachment}
                      aria-label="Remove attachment"
                    />
                  }
                >
                  {attachment.type === 'image' ? '🖼️ Image' : '🎵 Audio'} attached
                </Badge>
              </Group>
            )}

            <Group align="flex-end" gap="sm">
              {/* File upload buttons - only show for vision-capable models */}
              <Tooltip
                label={
                  supportsVision
                    ? 'Attach image (PNG, JPEG, GIF, WebP)'
                    : "Selected model doesn't support image input"
                }
              >
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={attachmentDisabled}
                  size="lg"
                  style={{ opacity: supportsVision ? 1 : 0.3 }}
                >
                  <IconPhoto size={18} />
                </ActionIcon>
              </Tooltip>

              <Tooltip
                label={
                  supportsVision
                    ? 'Attach audio (WAV, MP3)'
                    : "Selected model doesn't support audio input"
                }
              >
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={() => audioInputRef.current?.click()}
                  disabled={attachmentDisabled}
                  size="lg"
                  style={{ opacity: supportsVision ? 1 : 0.3 }}
                >
                  <IconMicrophone size={18} />
                </ActionIcon>
              </Tooltip>

              <Textarea
                {...form.getInputProps('message')}
                placeholder={placeholder}
                autosize
                minRows={1}
                maxRows={6}
                flex={1}
                disabled={disabled}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    const result = form.validate();
                    if (!result.hasErrors) {
                      handleSubmit(form.values);
                    }
                  }
                }}
              />

              <Tooltip label={submitTooltip}>
                {isStreaming ? (
                  <ActionIcon size="lg" variant="filled" color="red" onClick={onStopStreaming}>
                    <IconPlayerStop size={18} />
                  </ActionIcon>
                ) : (
                  <ActionIcon
                    type="submit"
                    size="lg"
                    variant="filled"
                    color="blue"
                    disabled={sendDisabled}
                  >
                    {isCooldown && secondsLeft > 0 ? (
                      <Text size="xs" fw={700}>
                        {secondsLeft}
                      </Text>
                    ) : (
                      <IconSend size={18} />
                    )}
                  </ActionIcon>
                )}
              </Tooltip>
            </Group>
          </form>

          {/* Rate limit warning message */}
          {status.kind === 'rate_limited' && (
            <Text size="xs" c="orange" mt="xs" ta="center" fw={500}>
              ⚠️ {status.reason}
            </Text>
          )}

          <Group justify="space-between" align="center" mt="xs">
            <Group gap="xs" align="center">
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => setSaveChatModalOpen(true)}
                disabled={messages.length === 0}
                aria-label="Save chat"
                size="sm"
              >
                <IconDownload size={16} />
              </ActionIcon>

              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => setSettingsModalOpen(true)}
                disabled={!selectedModel}
                aria-label="Settings"
                size="sm"
              >
                <IconSettings size={16} />
              </ActionIcon>

              <CharacterSelector
                selectedCharacter={selectedCharacter ?? null}
                onCharacterSelect={onCharacterSelect || (() => {})}
                onModelSelect={onModelSelect || (() => {})}
                models={models}
                characters={characters}
                disabled={messages.length > 0}
                isLoading={false}
                isDesktop={true}
              />
            </Group>

            <Text size="xs" c="dimmed" ta="center">
              {isAuthenticated ? 'Authenticated chat' : 'Public chat'}
            </Text>

            <ModelSelector
              models={models}
              selectedModel={selectedModel}
              onModelSelect={onModelSelect || (() => {})}
              isLoading={isLoadingModels}
              isLoggedIn={isAuthenticated}
              isDesktop={true}
            />
          </Group>
        </Box>
      </Center>

      <SaveChatModal
        opened={saveChatModalOpen}
        onClose={() => setSaveChatModalOpen(false)}
        messages={messages}
        selectedCharacter={selectedCharacter}
        characters={characters}
      />

      <SettingsModal
        opened={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        selectedModel={models.find((m) => m.id === selectedModel)}
        onSettingsChange={onReasoningSettingsChange || (() => {})}
        initialSettings={currentReasoningSettings}
        onChatSettingsChange={onChatSettingsChange || (() => {})}
        initialChatSettings={currentChatSettings}
        onWebSearchSettingsChange={onWebSearchSettingsChange || (() => {})}
        initialWebSearchSettings={currentWebSearchSettings}
      />
    </>
  );
}
